// ─── «Tutta la serie», nella scheda di una prenotazione che si ripete ────────
//
// 🔁⭐⭐ Lista del 28/09/2026, voce 5 (mockup approvato, 1A 2A 3A). 🗣️ Dal committente:
// *«una sottosezione dentro la scheda di prenotazione che si attiva flaggando una casella … sia per
// prenotarla sia per modificarla»* · *«modificare quella scelta o la serie»* · *«per modificare la
// serie mi devi dare la possibilità di annullare alcune o tutte le date fissate»*.
//
// Tre gesti, nella stessa sezione della prenotazione nuova (⇒ `SezioneRicorrente`):
//   · **cambiare la fine, le ore, i giorni o il ritmo**: prima di «Salva» si legge cosa nasce, cosa
//     si toglie e chi riceve l'avviso — lo stesso conto che poi fa il server (`pianoDellaSerie`);
//   · **annullare le date scelte**, una, alcune o tutte, dall'elenco con le caselle;
//   · **togliere la spunta**: questa resta, e le date dopo si annullano.
// ⛔ Il passato non si tocca: le date già giocate si vedono in grigio, senza casella.

import { useEffect, useState } from 'react';
import {
  comeSiRipete,
  etichettaCadenza,
  oraPiuMinuti,
  pianoDellaSerie,
  stessaRegola,
  type SchedaPrenotazione,
  type VoltaDellaSerie,
} from '@pmo/shared';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { giornoSenzaAnno } from '../../lib/time.js';
import { euro } from '../../routes/impostazioni/comune.js';
import { SezioneRicorrente, regolaDa, valoriDa, type ValoriRipetizione } from './pezzi-scheda.js';
import { usaAnnullaVolte, usaModificaSerie, usaVolteDellaSerie } from './dati.js';

const GIORNI_BREVI = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];

function giornoBreve(data: string): string {
  const d = new Date(`${data}T00:00:00Z`);
  return `${GIORNI_BREVI[(d.getUTCDay() + 6) % 7]} ${giornoSenzaAnno(data)}`;
}

const elenco = (date: readonly string[]): string => date.map(giornoSenzaAnno).join(' · ');

export function SerieDellaScheda({
  p,
  chiudi,
}: {
  p: SchedaPrenotazione;
  chiudi(): void;
}): React.JSX.Element {
  const serie = p.serie!;
  const volte = usaVolteDellaSerie(serie.id, true);
  const cambiaSerie = usaModificaSerie(serie.id);
  const annulla = usaAnnullaVolte(serie.id, p.id);

  const iniziali = valoriDa(serie);
  const daCapo = (): ValoriRipetizione =>
    iniziali ?? { attiva: true, famiglia: 'settimane', passo: 1, giorni: [], fino: serie.fino };
  const [v, setV] = useState<ValoriRipetizione>(daCapo);
  const [ora, setOra] = useState(p.time);
  const [durata, setDurata] = useState(p.durationMinutes);
  const [scelte, setScelte] = useState<ReadonlySet<string>>(new Set());

  // Dopo un «Salva» la serie cambia: la sezione riparte da com'è adesso.
  const firma = `${serie.cadenza}|${serie.passo ?? ''}|${(serie.giorni ?? []).join()}|${serie.fino}|${p.time}|${p.durationMinutes}`;
  useEffect(() => {
    setV(daCapo());
    setOra(p.time);
    setDurata(p.durationMinutes);
  }, [firma]);

  const items: readonly VoltaDellaSerie[] = volte.data?.items ?? [];
  const prima = { cadenza: serie.cadenza, giorni: serie.giorni, passo: serie.passo };
  const dopo = iniziali ? regolaDa(v) : prima;
  const regolaCambiata = iniziali !== null && !stessaRegola(serie.dal, prima, dopo);
  const oreCambiate = ora !== p.time || durata !== p.durationMinutes;
  const fineCambiata = v.fino !== serie.fino;
  const piano = volte.data
    ? pianoDellaSerie({ dal: serie.dal, prima, dopo, fino: v.fino, volte: items, oggi: volte.data.oggi })
    : null;
  const conGente = piano
    ? items.filter((x) => piano.tolte.includes(x.date) && x.persone > 0).map((x) => x.date)
    : [];
  const future = items.filter((x) => !x.passata);
  const dopoQuesta = future.filter((x) => x.date > p.date);

  // Se fra le date che si tolgono c'è anche questa, la sua scheda a cose fatte si chiude.
  const vaViaAncheQuesta = piano?.tolte.includes(p.date) ?? false;
  const salva = () =>
    cambiaSerie.mutate(
      {
        fino: v.fino,
        ...(regolaCambiata
          ? { regola: { cadenza: dopo.cadenza, giorni: dopo.giorni ?? [], passo: dopo.passo ?? null } }
          : {}),
        ...(oreCambiate ? { time: ora, durationMinutes: durata } : {}),
      },
      { onSuccess: () => vaViaAncheQuesta && chiudi() },
    );

  const riassunto = (
    <div className="riassunto-ricorrente">
      <p>
        Si ripete <strong>{etichettaCadenza(dopo)}</strong>, {comeSiRipete(serie.dal, dopo)}, dalle {ora} alle{' '}
        {oraPiuMinuti(ora, durata)}, fino al {giornoSenzaAnno(v.fino)}.
      </p>
      {piano && (regolaCambiata || fineCambiata || oreCambiate) && (
        <p>
          <strong>Salvando:</strong>{' '}
          {[
            piano.nate.length > 0 &&
              `${piano.nate.length === 1 ? 'nasce una data' : `nascono ${piano.nate.length} date`} (${elenco(piano.nate)})`,
            piano.tolte.length > 0 &&
              `${piano.tolte.length === 1 ? 'si toglie una data' : `si tolgono ${piano.tolte.length} date`} (${elenco(piano.tolte)})`,
            oreCambiate &&
              `le date che devono ancora venire passano dalle ${ora} alle ${oraPiuMinuti(ora, durata)}`,
          ]
            .filter(Boolean)
            .join('; ') || 'la serie resta con le stesse date'}
          .
          {conGente.length > 0 && (
            <> ⚠️ {elenco(conGente)}: ci sono già dei giocatori, e ricevono l’avviso.</>
          )}
          {vaViaAncheQuesta && (
            <> Fra queste c’è anche il {giornoSenzaAnno(p.date)}: salvando, la sua scheda si chiude.</>
          )}
          {oreCambiate && <> Se a quell’ora il campo è occupato, quella data resta com’è e te lo dico.</>}
        </p>
      )}
      <div className="azioni">
        <button
          type="button"
          disabled={!(regolaCambiata || oreCambiate || fineCambiata) || cambiaSerie.isPending || !piano}
          onClick={salva}
        >
          {cambiaSerie.isPending ? 'Un attimo…' : 'Salva le modifiche'}
        </button>
      </div>
    </div>
  );

  const nScelte = scelte.size;
  const incassateScelte = items.filter((x) => scelte.has(x.id) && x.incassatoCents > 0);
  const annullaLeScelte = () =>
    annulla.mutate(
      { ids: [...scelte] },
      {
        onSuccess: () => {
          const anche = scelte.has(p.id);
          setScelte(new Set());
          if (anche) chiudi();
        },
      },
    );

  return (
    <>
      <SezioneRicorrente
        dal={serie.dal}
        ora={ora}
        cambiaOra={setOra}
        durata={durata}
        cambiaDurata={setDurata}
        v={v}
        cambia={(c) => setV((x) => ({ ...x, ...c }))}
        regolaFissa={iniziali ? undefined : `${etichettaCadenza(prima)}, ${comeSiRipete(serie.dal, prima)}`}
        sotto={riassunto}
      />

      {!v.attiva && (
        <div className="riassunto-ricorrente">
          {dopoQuesta.length > 0 ? (
            <p>
              Togliendo la spunta, <strong>questa prenotazione resta</strong> e{' '}
              {dopoQuesta.length === 1
                ? 'la data dopo si annulla'
                : `le ${dopoQuesta.length} date dopo si annullano`}{' '}
              ({elenco(dopoQuesta.map((x) => x.date))}). Chi ci giocava riceve l’avviso.
            </p>
          ) : (
            <p>Questa è l’ultima data della serie: togliendo la spunta, la serie finisce qui.</p>
          )}
          <div className="azioni">
            <button
              type="button"
              className="pericolo"
              disabled={cambiaSerie.isPending}
              onClick={() => cambiaSerie.mutate({ fino: p.date })}
            >
              {cambiaSerie.isPending ? 'Un attimo…' : 'Salva: resta solo questa'}
            </button>
            <button type="button" className="piatto" onClick={() => setV((x) => ({ ...x, attiva: true }))}>
              Lascia stare
            </button>
          </div>
        </div>
      )}

      <Guasto errore={cambiaSerie.error} />
      {cambiaSerie.data && (
        <p className="tenue piccolo">
          ✅ {cambiaSerie.data.nate > 0 && <>Ne sono nate {cambiaSerie.data.nate}. </>}
          {cambiaSerie.data.tolte > 0 && <>Ne sono state tolte {cambiaSerie.data.tolte}. </>}
          Adesso sono {cambiaSerie.data.quante}.
          {cambiaSerie.data.saltate.length > 0 && (
            <>
              {' '}
              ⚠️ Saltate:{' '}
              {cambiaSerie.data.saltate
                .map((g) => `${giornoSenzaAnno(g.date)} (${g.perche.replace(/\.$/, '')})`)
                .join(' · ')}
              .
            </>
          )}
          {cambiaSerie.data.ferme.length > 0 && (
            <>
              {' '}
              ⚠️ Restate all’ora di prima:{' '}
              {cambiaSerie.data.ferme
                .map((g) => `${giornoSenzaAnno(g.date)} (${g.perche.replace(/\.$/, '')})`)
                .join(' · ')}
              .
            </>
          )}
        </p>
      )}

      {/* ── 🗑️ le date della serie, con le caselle ───────────────────────── */}
      {v.attiva && (
        <div className="date-della-serie">
          <div className="testa-date">
            <strong>Le date della serie</strong>
            <span className="spazio" />
            <button
              type="button"
              className="come-link"
              onClick={() => setScelte(new Set(future.map((x) => x.id)))}
            >
              Seleziona tutte
            </button>
            <button type="button" className="come-link" onClick={() => setScelte(new Set())}>
              Nessuna
            </button>
          </div>
          {volte.isPending ? (
            <Attesa cosa="le date della serie" />
          ) : (
            <>
              <Guasto errore={volte.error} />
              <ul className="elenco-date">
                {items.map((x) => (
                  <li key={x.id} className={x.passata ? 'passata' : undefined}>
                    <label>
                      <input
                        type="checkbox"
                        disabled={x.passata}
                        checked={scelte.has(x.id)}
                        onChange={(e) => {
                          const n = new Set(scelte);
                          if (e.target.checked) n.add(x.id);
                          else n.delete(x.id);
                          setScelte(n);
                        }}
                      />
                      <span className="quando">
                        {giornoBreve(x.date)} · {x.time}
                        {x.id === p.id && <span className="tenue"> (questa)</span>}
                      </span>
                      <span className="tenue piccolo cosa-c-e">
                        {x.passata
                          ? 'giocata'
                          : [
                              x.persone > 0 && `👥 ${x.persone}`,
                              x.incassatoCents > 0 && `💶 ${euro(x.incassatoCents)}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
          {incassateScelte.length > 0 && (
            <p className="tenue piccolo">
              ⚠️{' '}
              {incassateScelte
                .map((x) => `${giornoSenzaAnno(x.date)}: ${euro(x.incassatoCents)}`)
                .join(' · ')}{' '}
              già in cassa: restano, e si stornano a parte.
            </p>
          )}
          <Guasto errore={annulla.error} />
          <div className="azioni">
            <button
              type="button"
              className="pericolo"
              disabled={nScelte === 0 || annulla.isPending}
              onClick={annullaLeScelte}
            >
              {annulla.isPending
                ? 'Un attimo…'
                : nScelte === 0
                  ? 'Spunta le date da annullare'
                  : nScelte === future.length && nScelte > 1
                    ? `Sì, annulla tutte le ${nScelte}`
                    : nScelte === 1
                      ? 'Sì, annulla quella scelta'
                      : `Sì, annulla le ${nScelte} scelte`}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
